import React, { useEffect } from 'react';
import { Img } from 'react-image';
import { Button, Spinner, Tooltip } from 'flowbite-react';
import { Navbar } from 'flowbite-react';

import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { interviewScreenInstructions } from 'src/utils/interviewScreenInstructions';
import { useUserContext } from 'src/context/UserProvider';
import { useGetMeetingByIdQuery } from 'src/hooks/meeting.hooks';
import { MeetingType, UIMeeting } from 'types';
import { MeetingJoineeStatus, MeetingStatus } from 'types';
import { createMeetingLog, startMeeting } from 'src/apis/meeting';
import { get } from 'lodash';
import { useToast } from 'src/context/ToastProvider';
import { useThemeContext } from 'src/context/ThemeProvider';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';

type Props = {
	meeting: UIMeeting;
	isHost: boolean;
};

const InterviewNotStartedScreen = ({ meeting, isHost }: Props) => {
	const queryClient = useQueryClient();
	const sessionData = useUserContext();
	const navigate = useNavigate();
	const toast = useToast();
	const { themeMode } = useThemeContext();

	const meetingInfo = useGetMeetingByIdQuery(meeting._id);
	const startMeetingFn = useMutation({
		mutationKey: 'startMeeting',
		mutationFn: startMeeting,
		onSuccess() {
			queryClient.refetchQueries(['getMeetingById', meeting._id]);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const addUserMeetingLog = useMutation({
		mutationKey: 'createMeetingLog',
		mutationFn: createMeetingLog,
	});

	const leaveMeeting = () => {
		if (sessionData.user?._id && meeting) {
			const payload = {
				participant: sessionData.user._id,
				status: MeetingJoineeStatus.LEFT,
				meetingId: meeting.dyteMeetingId,
			};
			addUserMeetingLog.mutate(payload);
		}
	};

	useEffect(() => {
		let checkingIfMeetingHasStarted: any = '';
		if (meeting.meetingStatus !== MeetingStatus.STARTED) {
			checkingIfMeetingHasStarted = setInterval(() => {
				meetingInfo.refetch();
			}, 5000);
		} else {
			clearInterval(checkingIfMeetingHasStarted);
		}
		return () => {
			clearInterval(checkingIfMeetingHasStarted);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting]);

	useEffect(() => {
		const currentTime = moment();
		const meetingStartTime = moment(meeting.startTime);
		if (meeting.meetingStatus !== MeetingStatus.STARTED) {
			if (
				currentTime.isSameOrAfter(meetingStartTime) &&
				(isHost || meeting.isAIInterview || meeting.meetingType === MeetingType.Assessment)
			) {
				startMeetingFn.mutate(meeting._id);
			}
		}
	}, [meeting.startTime]);

	const meetingDateTime = moment(meeting.startTime);
	const date = meetingDateTime.format('DD MMM YYYY');
	const time = meetingDateTime.format('hh:mm a');

	const waitingTime = moment(meeting.startTime).add(10, 'minutes').format('hh:mm a');

	const disableStarting = moment().isBefore(meetingDateTime.subtract(2, 'minutes'));

	return (
		<div className="h-screen w-screen dark:text-white">
			<Navbar
				className={`w-full rounded-none !px-[45px] sm:!px-[10px] bg-white dark:bg-[#18222D]`}
				fluid={true}
				rounded={true}
			>
				<div className="flex items-center gap-8 ">
					<div
						onClick={() => {
							leaveMeeting();
							// navigate('/dashboard');
							navigate('/my-applications');
						}}
						className="flex items-center"
					>
						<Img
							loading="lazy"
							src={
								themeMode === 'dark'
									? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
									: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
							}
							width="110"
							height="32px"
							alt="LevelUp Logo"
						/>
					</div>
				</div>
			</Navbar>
			<div className="my-60 flex flex-col items-center justify-center gap-2 dark:text-white">
				{startMeetingFn.isLoading && moment().isSameOrAfter(moment(meeting.startTime)) ? (
					<div className="flex items-center h-full w-full justify-center">
						{/* <Spinner /> */}
						<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
					</div>
				) : (
					<p className=" text-2xl font-bold">
						Technical Round starts at <span className="text-blue-600 dark:text-blue-400">{time}</span> on{' '}
						<span className="text-blue-600 dark:text-blue-400">{date}</span> and is for about{' '}
						<span className="text-blue-600 dark:text-blue-400">{meeting.duration} minutes</span>.
					</p>
				)}
				{isHost || meeting.isAIInterview || meeting.meetingType === MeetingType.Assessment ? (
					!(startMeetingFn.isLoading && moment().isSameOrAfter(moment(meeting.startTime))) ? (
						<Tooltip
							placement="bottom"
							content="The interview can only be started up to 2 minutes before the scheduled start time."
						>
							<Button
								color="purplePlaced"
								onClick={() => {
									startMeetingFn.mutate(meeting._id);
								}}
								disabled={startMeetingFn.isLoading || disableStarting}
							>
								{startMeetingFn.isLoading ? <Spinner size="sm" light={true} /> : 'Start Interview'}
							</Button>
						</Tooltip>
					) : null
				) : (
					<div className="flex flex-col items-center">
						{!meeting.isAIInterview && (
							<>
								<p className=" text-base">The meeting will be started by the interviewer at the mentioned time.</p>

								<br />

								<p className="text-lg">
									We request you to wait till <span className="font-bold text-blue-400">{waitingTime}</span> for our
									interviewer to join before dropping off.
								</p>
								<br />
							</>
						)}
						<div className="flex flex-col items-center">
							<p>Meanwhile, please ensure the following:</p>
							{interviewScreenInstructions.map(i => (
								<p key={i}>{i}</p>
							))}
						</div>
						<Button
							color="purplePlaced"
							className="mt-6"
							onClick={() => {
								leaveMeeting();
								navigate('/interviews');
							}}
						>
							Ok, Got it
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default InterviewNotStartedScreen;
